const AUTH_REQUIRED_MESSAGE = "Sign in to use transcription and prompt features.";
const MIC_PERMISSION_MESSAGE = "Please allow microphone access to start recording.";
const MIC_NOT_FOUND_MESSAGE = "No microphone was found. Connect a microphone and try again.";
const SECURE_CONTEXT_MESSAGE = "Microphone access requires HTTPS or localhost.";
const NETWORK_MESSAGE = "Connection lost. Check your internet and try again.";
const TRANSCRIPTION_MESSAGE =
  "Transcription is temporarily unavailable. Please try again shortly.";
const PROMPT_MESSAGE =
  "Prompt processing is temporarily unavailable. Please try again shortly.";
export const USAGE_LIMIT_ERROR_CODE = "USAGE_LIMIT_EXCEEDED";
export const USAGE_LIMIT_MESSAGE =
  "You've used all your free transcription minutes this week. Upgrade to ZT Pro to keep going.";

const USAGE_LIMIT_SERVER_CODES = new Set([
  "free_plan_minutes_exhausted",
  "subscription_error",
  USAGE_LIMIT_ERROR_CODE,
]);
const USAGE_LIMIT_MESSAGE_PATTERN =
  /free plan minutes|minutes are exhausted|minutes exhausted|upgrade to pro plan/i;
const USAGE_LIMIT_CLOSE_REASON_PATTERN =
  /free plan minutes|minutes exhausted|usage limit/i;

const readStatus = (value) => value?.status || value?.response?.status || null;
const readErrorCode = (value) =>
  value?.errorCode ||
  value?.code ||
  value?.error?.errorCode ||
  value?.error?.code ||
  value?.response?.data?.errorCode ||
  value?.response?.data?.code ||
  null;
const readErrorType = (value) =>
  value?.error?.type || value?.type || value?.response?.data?.type || null;
const readName = (value) => value?.name || null;
const readMessage = (value) =>
  String(
    value?.message ||
    value?.error?.message ||
    value?.response?.data?.error ||
    value?.response?.data?.message ||
    ""
  ).trim();

const isOffline = () =>
  typeof navigator !== "undefined" &&
  Object.prototype.hasOwnProperty.call(navigator, "onLine") &&
  navigator.onLine === false;

const matches = (message, pattern) => pattern.test(String(message || ""));

export const isUsageLimitSignal = (value) => {
  if (!value || typeof value !== "object") return false;

  const closeCode = Number(value.closeCode ?? value.code);
  const closeReason = value.closeReason ?? value.reason;
  if (closeCode === 1008 && matches(closeReason, USAGE_LIMIT_CLOSE_REASON_PATTERN)) {
    return true;
  }

  const errorCode = readErrorCode(value);
  if (errorCode && USAGE_LIMIT_SERVER_CODES.has(String(errorCode))) return true;

  const errorType = readErrorType(value);
  if (errorType && USAGE_LIMIT_SERVER_CODES.has(String(errorType))) return true;

  const message = readMessage(value);
  if (matches(message, USAGE_LIMIT_MESSAGE_PATTERN)) return true;

  return false;
};

export const createUsageLimitError = (payload) => {
  const message =
    (payload && (payload.message || payload.error?.message)) || USAGE_LIMIT_MESSAGE;
  const err = new Error(message);
  err.errorCode = USAGE_LIMIT_ERROR_CODE;
  err.details = payload || null;
  return err;
};

export const formatAppError = (value, { feature = "transcription" } = {}) => {
  const status = readStatus(value);
  const errorCode = readErrorCode(value);
  const name = readName(value);
  const message = readMessage(value);

  if (isUsageLimitSignal(value)) {
    return USAGE_LIMIT_MESSAGE;
  }

  if (
    status === 401 ||
    status === 403 ||
    errorCode === "AUTH_REQUIRED" ||
    matches(message, /no auth cookies|sign in to continue|unable to fetch websocket token/i)
  ) {
    return AUTH_REQUIRED_MESSAGE;
  }

  if (
    errorCode === "MIC_PERMISSION_DENIED" ||
    name === "NotAllowedError" ||
    matches(message, /microphone.*denied|permission.*microphone|allow microphone/i)
  ) {
    return MIC_PERMISSION_MESSAGE;
  }

  if (
    errorCode === "MIC_NOT_FOUND" ||
    name === "NotFoundError" ||
    matches(message, /no microphone|microphone.*not found/i)
  ) {
    return MIC_NOT_FOUND_MESSAGE;
  }

  if (
    errorCode === "SECURE_CONTEXT_REQUIRED" ||
    matches(message, /https|secure context/i)
  ) {
    return SECURE_CONTEXT_MESSAGE;
  }

  if (
    isOffline() ||
    errorCode === "NETWORK_ERROR" ||
    matches(message, /network issue|connection error|websocket connection error|ws open timeout|timeout/i)
  ) {
    return NETWORK_MESSAGE;
  }

  if (
    errorCode === "PROMPT_UNAVAILABLE" ||
    (feature === "prompt" && matches(message, /prompt/i))
  ) {
    return PROMPT_MESSAGE;
  }

  if (
    errorCode === "TRANSCRIPTION_UNAVAILABLE" ||
    feature === "transcription" ||
    matches(message, /transcription|audio processing|realtime/i)
  ) {
    return TRANSCRIPTION_MESSAGE;
  }

  return message || "Something went wrong. Please try again.";
};

export const createSecureContextError = () => {
  const error = new Error("Mic requires HTTPS (or localhost) secure context.");
  error.errorCode = "SECURE_CONTEXT_REQUIRED";
  return error;
};
